import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const DARK_THEME = {
  // Backgrounds
  bg: '#050507',
  bgCard: 'rgba(20, 22, 30, 0.6)',
  bgSurface: 'rgba(255, 255, 255, 0.025)',
  bgElevated: '#0f0f14',
  bgInput: 'rgba(0, 0, 0, 0.2)',

  // Borders
  border: 'rgba(255, 255, 255, 0.08)',
  borderHover: 'rgba(255, 255, 255, 0.15)',

  // Text
  textPrimary: '#F8FAFC',
  textSecondary: '#94A3B8',
  textMuted: '#475569',
  textInverse: '#0F172A',

  // Accents (same in both themes)
  accent: '#3B82F6',
  accentBlue: '#0a84ff',
  accentGreen: '#30D158',
  accentRed: '#ff453a',
  accentPurple: '#bf5af2',
  accentYellow: '#ffd60a',

  // Navigation
  navBg: 'rgba(17, 17, 17, 0.7)',
  navBorder: 'rgba(255, 255, 255, 0.1)',
  navText: 'rgba(255, 255, 255, 0.7)',
  navTextActive: '#ffffff',
  navIconBg: 'rgba(255, 255, 255, 0.1)',

  // Input
  inputBg: 'rgba(0, 0, 0, 0.2)',
  inputBorder: 'rgba(255, 255, 255, 0.1)',
  inputText: '#F8FAFC',
  inputPlaceholder: '#888',

  // SplashCursor
  splashBackColor: { r: 0.02, g: 0.02, b: 0.03 },

  // Overlay
  overlay: 'rgba(0, 0, 0, 0.7)',
  modalBg: '#1E1E23',
};

const LIGHT_THEME = {
  // Backgrounds
  bg: '#F0F2F5',
  bgCard: 'rgba(255, 255, 255, 0.9)',
  bgSurface: 'rgba(0, 0, 0, 0.03)',
  bgElevated: '#ffffff',
  bgInput: 'rgba(255, 255, 255, 0.9)',

  // Borders
  border: 'rgba(0, 0, 0, 0.08)',
  borderHover: 'rgba(0, 0, 0, 0.2)',

  // Text
  textPrimary: '#0F172A',
  textSecondary: '#475569',
  textMuted: '#94A3B8',
  textInverse: '#F8FAFC',

  // Accents (same in both themes)
  accent: '#3B82F6',
  accentBlue: '#0a84ff',
  accentGreen: '#16A34A',
  accentRed: '#DC2626',
  accentPurple: '#7C3AED',
  accentYellow: '#D97706',

  // Navigation
  navBg: 'rgba(255, 255, 255, 0.88)',
  navBorder: 'rgba(0, 0, 0, 0.1)',
  navText: 'rgba(15, 23, 42, 0.65)',
  navTextActive: '#0F172A',
  navIconBg: 'rgba(0, 0, 0, 0.06)',

  // Input
  inputBg: 'rgba(255, 255, 255, 0.9)',
  inputBorder: 'rgba(0, 0, 0, 0.15)',
  inputText: '#0F172A',
  inputPlaceholder: '#94A3B8',

  // SplashCursor
  splashBackColor: { r: 0.94, g: 0.95, b: 0.96 },

  // Overlay
  overlay: 'rgba(0, 0, 0, 0.5)',
  modalBg: '#ffffff',
};

const CSS_VARS = {
  '--bg-deep-void': 'bg',
  '--bg-card': 'bgCard',
  '--bg-calendar-picker': 'bgElevated',
  '--glass-panel': 'bgCard',
  '--glass-hover': 'bgSurface',
  '--text-primary': 'textPrimary',
  '--text-secondary': 'textSecondary',
  '--text-muted': 'textMuted',
  '--accent-primary': 'accent',
};

const LIGHT_CSS_OVERRIDES = {
  '--bg-deep-void': '#F0F2F5',
  '--bg-card': 'rgba(255, 255, 255, 0.9)',
  '--bg-calendar-picker': '#ffffff',
  '--glass-panel': 'rgba(255, 255, 255, 0.75)',
  '--glass-hover': 'rgba(240, 242, 245, 0.95)',
  '--text-primary': '#0F172A',
  '--text-secondary': '#475569',
  '--text-muted': '#94A3B8',
  '--shadow-glass': '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
};

const DARK_CSS_OVERRIDES = {
  '--bg-deep-void': '#050507',
  '--bg-card': 'rgba(20, 22, 30, 0.6)',
  '--bg-calendar-picker': '#1a1a1f',
  '--glass-panel': 'rgba(20, 22, 30, 0.6)',
  '--glass-hover': 'rgba(30, 35, 50, 0.7)',
  '--text-primary': '#F8FAFC',
  '--text-secondary': '#94A3B8',
  '--text-muted': '#475569',
  '--shadow-glass': '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
};

const ThemeContext = createContext({
  isDark: true,
  toggleTheme: () => {},
  theme: DARK_THEME,
});

const applyTheme = (isDark) => {
  const overrides = isDark ? DARK_CSS_OVERRIDES : LIGHT_CSS_OVERRIDES;
  const root = document.documentElement;
  Object.entries(overrides).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
  root.setAttribute('data-theme', isDark ? 'dark' : 'light');
};

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    try {
      const saved = localStorage.getItem('talzo-theme');
      return saved ? saved === 'dark' : false;
    } catch {
      return true;
    }
  });

  useEffect(() => {
    applyTheme(isDark);
  }, [isDark]);

  const toggleTheme = useCallback(() => {
    setIsDark((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('talzo-theme', next ? 'dark' : 'light');
      } catch {}
      return next;
    });
  }, []);

  const theme = isDark ? DARK_THEME : LIGHT_THEME;

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme, theme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export { DARK_THEME, LIGHT_THEME };
